/**
 * Laying a RUN of tiles with one drag, instead of one deliberate tap per tile.
 *
 * OWNER (2026-08-15): "you can build the roads, so you can choose — plot, and then
 * go one after another block by block creating the plots, now it's hard." Roads and
 * fences already stay armed between placements (see placeBuilding's buildMin rule),
 * but every tile still cost a separate tap, and a bed cost a tap PLUS a walk. On a
 * phone that is thirty taps to pave a path.
 *
 * THE HARD PART is that dragging is also how the camera pans, and the two gestures
 * are physically identical: finger down, finger moves, finger up. There is no
 * timing or distance rule that separates them without stealing one from the other
 * (a "hold 300ms first" rule makes painting feel broken; a distance rule makes
 * panning paint a streak of roads across the map). So the discriminator is INTENT
 * DECLARED BEFORE THE GESTURE, and it takes two things at once:
 *
 *   1. a placement tool is armed — build mode with an instant kind, or the hoe;
 *   2. the tile the finger LANDS on is one that tool would legally act on.
 *
 * Both together, because (1) alone is not enough: the hoe is selected most of the
 * time, and locking out panning while it is would be worse than the problem. With
 * (2), a drag that starts on water, on a tree, on your own house or on an occupied
 * tile still pans exactly as it always did — which is what a player does when he
 * means to pan, since he grabs empty screen, not the one buildable tile.
 *
 * Everything here is pure so the decision can be pinned in a test. jsdom has no
 * layout engine and cannot drive Pixi, so the pointer plumbing itself (pointerdown
 * → move → up on the stage, the camera pan lock) is NOT covered by tests — only the
 * rules it consults are.
 */

export type PaintKind = 'build' | 'till';

/**
 * How many tiles one gesture may paint.
 *
 * TWO ceilings sit above a painted run, and the first one was missed:
 *
 *  - THE CLIENT FUNNEL (dispatch.ts) allows 10 actions/sec across ALL types and
 *    then throws ActionDropped, which guard() swallows without a word. Firing one
 *    request per tile inside the pointermove handler therefore lost every tile past
 *    the tenth of a fast drag — silently, and unrecoverably, because planPaint had
 *    already marked those tiles done so the gesture never retried them. That is
 *    what {@link PAINT_PACE_MS} exists to prevent; the cap below does NOT help with
 *    it, because the cap bounds the run, not its rate.
 *  - THE SERVER's bulk placement budget is 150 ops/minute (build.limits.ts) and is
 *    shared with demolition. 40 is far more than any single deliberate run and
 *    leaves room for three of them inside one window; lift the finger and drag
 *    again to continue.
 */
export const PAINT_GESTURE_MAX = 40;

/**
 * Minimum gap between two placements of a painted run, in ms.
 *
 * 200ms = 5 requests/sec, half the client funnel's 10/sec global cap, so a run
 * still leaves the player room to chop, harvest or walk mid-drag without either
 * stream being dropped. It is also below the server's bulk budget (150/min ≈
 * 2.5/sec sustained), which a 40-tile run cannot exhaust in its 8 seconds.
 *
 * The pace is why the queue exists at all: the tiles are decided during the drag
 * and DRAINED afterwards, one at a time, instead of burst-fired from pointermove.
 */
export const PAINT_PACE_MS = 200;

/**
 * Does this press begin a paint, and of what? `null` means "leave it to the camera",
 * which is the answer for every gesture that is not an explicit, legal placement.
 *
 * `legal` is the tool's OWN blocker answering null for the anchor tile — buildBlocker
 * for build mode, tillBlocker for the hoe — so this never re-implements eligibility.
 */
export function paintStart(a: {
  buildMode: string | null;
  /** A building is being carried to a new tile. */
  moving: boolean;
  /** buildMin === 0 — the kinds that are laid in runs (fence, road, path, props). */
  instant: boolean;
  selectedTool: string | null;
  hoeIntent: 'auto' | 'clear';
  legal: boolean;
}): PaintKind | null {
  if (!a.legal) return null;
  // A move relocates ONE building. There is no run to paint, and dragging it would
  // scatter the same building across the map.
  if (a.moving) return null;
  if (a.buildMode) {
    // Timed kinds (huts, stations, bridges) cost materials, take minutes and can
    // only be built one at a time — placeBuilding even disarms after them. Painting
    // a row of them would be a row of refusals, and they bill the tight STRUCTURE
    // budget (12/min) rather than the bulk one.
    return a.instant ? 'build' : null;
  }
  // Alt-tap is "grass this bed over" — destructive, confirmed, and explicitly never
  // a run gesture (see HoeIntent in input.ts).
  if (a.selectedTool === 'hoe' && a.hoeIntent === 'auto') return 'till';
  return null;
}

/**
 * Every tile the pointer crossed between two samples, start EXCLUSIVE, end inclusive.
 *
 * A pointermove is sampled at most once a frame, and a fast drag jumps four or five
 * tiles between samples — sampling alone would paint a dotted line with holes in it,
 * which is worse than no painting at all. Bresenham fills the gap so the run follows
 * the stroke the player actually drew.
 */
export function tileLine(ax: number, ay: number, bx: number, by: number): [number, number][] {
  const out: [number, number][] = [];
  const dx = Math.abs(bx - ax), dy = Math.abs(by - ay);
  const sx = ax < bx ? 1 : -1, sy = ay < by ? 1 : -1;
  let err = dx - dy, x = ax, y = ay;
  // Bounded: a pointer cannot jump further than the viewport, and the caller caps
  // the plan anyway — but a runaway loop here would freeze the tab, so guard it.
  for (let i = 0; i < 512; i++) {
    if (x === bx && y === by) break;
    const e2 = 2 * err;
    if (e2 > -dy) { err -= dy; x += sx; }
    if (e2 < dx) { err += dx; y += sy; }
    out.push([x, y]);
  }
  return out;
}

/**
 * The tiles one pointermove contributes to the run.
 *
 * The ANCHOR (the tile the finger landed on) is deliberately NOT painted on
 * pointerdown: a press that never moves has to stay an ordinary tap, or every tap
 * would lose the behaviour it already has — notably the walk-to for an out-of-reach
 * bed. It joins the run on the first sample that leaves it, which is the first
 * moment the gesture is unmistakably a drag.
 */
export function strokeTiles(
  from: [number, number],
  to: [number, number],
  anchorPending: boolean,
): [number, number][] {
  const line = tileLine(from[0], from[1], to[0], to[1]);
  return anchorPending && line.length ? [from, ...line] : line;
}

export interface PaintPlan {
  /** Tiles to act on, in stroke order. */
  place: [number, number][];
  /** Distinct reasons tiles were refused, in the order they were first hit. */
  refusals: string[];
}

/**
 * Which of these tiles the run acts on, and what to say about the rest.
 *
 * `done` is the gesture's memory and is MUTATED with everything accepted: a drag
 * wanders back over tiles it already crossed constantly, and without this each
 * wobble would fire another request for a tile that is already paved.
 *
 * Refusals are collected as distinct strings rather than one per tile. Dragging
 * across a treeline is thirty refusals with three sentences between them; the caller
 * feeds these to notifyOnce, which says each one once and stays quiet after.
 */
export function planPaint(
  tiles: [number, number][],
  blocker: (tx: number, ty: number) => string | null,
  o: { done: Set<string>; budget: number },
): PaintPlan {
  const place: [number, number][] = [];
  const refusals: string[] = [];
  for (const [tx, ty] of tiles) {
    const k = `${tx},${ty}`;
    if (o.done.has(k)) continue;
    const why = blocker(tx, ty);
    if (why) {
      // Refused tiles are remembered too: the blocker's answer will not change
      // mid-gesture, so re-asking on every wobble is pure work for the same words.
      o.done.add(k);
      if (!refusals.includes(why)) refusals.push(why);
      continue;
    }
    if (place.length >= o.budget) break;
    o.done.add(k);
    place.push([tx, ty]);
  }
  return { place, refusals };
}

/**
 * Can the player pay for the NEXT tile of a build run?
 *
 * buildBlocker answers only map-shaped questions — it never looked at the wallet,
 * which was harmless for one deliberate tap (the HUD greys out an unaffordable kind
 * before you can arm it) and wrong for a run: a drag longer than the player can
 * afford queued every tile anyway, and each one came back as its own server 400 and
 * its own red toast. `spent` is the number of tiles of THIS run already queued,
 * because the bag is only re-read once the gesture is over — without it the run
 * would keep checking against the balance it had when the finger went down.
 *
 * `have === null` means the bag has not loaded yet; the server is the authority on
 * cost, so an unknown balance must not block a placement.
 */
export function affordBlocker(
  cost: { wood: number; stone: number },
  have: { wood: number; stone: number } | null,
  spent: number,
): string | null {
  if (!have) return null;
  const n = spent + 1;
  if (have.wood < cost.wood * n || have.stone < cost.stone * n) return 'Not enough materials 🪵';
  return null;
}

/**
 * Minimum gap between two beds of a painted hoe run, in ms.
 *
 * Deliberately LARGER than PAINT_PACE_MS and larger than dispatch.ts's 250ms
 * DEBOUNCE_MS, because field.till goes through guard() with no key: every bed of
 * the row shares one funnel slot, so two tills inside 250ms are the same slot and
 * the second is dropped outright. A build run does not have this problem — it is
 * keyed per tile — which is why the two paces are different numbers.
 */
export const TILL_PACE_MS = 300;
